/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {MarketAreaPlanningMarketMap} from '../models/market-area-planning-market-map.model';
import {PlanningMarket} from '../models/planning-market.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class PlanningMarketService {

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * sends a get call to get all planning market records
   */
  getPlanningMarkets(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}planning-market`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a post request to add a new planning market record
   * @param planningMarket The planning market to add
   */
  addPlanningMarket(planningMarket: PlanningMarket): Observable<any> {
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this.http.post<any>(`${this.configService.baseApiUrl}planning-market`, planningMarket, {headers}).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a put request to update a planning market
   * @param planningMarket The planning market to update
   */
  updatePlanningMarket(planningMarket: PlanningMarket): Observable<any> {
    const headers = new HttpHeaders();
    headers.set('Content-Type', 'application/json');
    return this.http.put<any>(`${this.configService.baseApiUrl}planning-market`, planningMarket, {headers}).pipe(
      catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to get a list of planning Market records based on region
   * @param region - string
   */
  getPlanningMarketsByRegion(region: string): Observable<PlanningMarket[]> {
    const params = new HttpParams().set('region', region);
    return this.http.get<PlanningMarket[]>(`${this.configService.baseApiUrl}planning-market`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Sends a get request to get a list of Market Area Planning Market Mapping records based on market area
   * @param mktArea - string
   */
  getPlanningMarketMappingsByMarketArea(mktArea: string): Observable<MarketAreaPlanningMarketMap[]> {
    const params = new HttpParams().set('mktArea', mktArea);
    return this.http.get<MarketAreaPlanningMarketMap[]>(`${this.configService.baseApiUrl}planning-market`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets a list of planning markets by either market area or subregion
   * @param mktArea the market area or subregion
   * @param indScenId The ID of the associated scenario
   * @param mktFlag boolean for whether to get by market area or not
   */
  getPlanningMarketsByMarketAreaOrSubregion(mktArea: string, indScenId: number, mktFlag: boolean): Observable<any[]> {
    const params = new HttpParams().set('market-area', mktArea).set('ind-scen-id', indScenId).set('mkt-flag', mktFlag);
    return this.http.get<any[]>(`${this.configService.baseApiUrl}planning-market`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets a list of planning markets by the provided vehicle scenario id
   * @param gmid The user's gmid
   * @param vehScenId the related vehicle scenario id
   */
  getPlanningMarketsByVehicleScenario(gmid: string, vehScenId: number) {
    const params = new HttpParams()
    .set('gmid', gmid)
    .set('veh-scen-id', vehScenId);
    return this.http.get<PlanningMarket[]>(`${this.configService.baseApiUrl}planning-market`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error has occurred.';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
